import { useEffect, useMemo, useState } from "react";
import { BookOpen, CheckCircle2, ChevronDown, Clock3, Lock, PlayCircle, RotateCcw } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useTutorial } from "@/tutorials/TutorialProvider";
import { getTutorialById } from "@/tutorials/tutorialRegistry";
import {
  TUTORIAL_MORE_TRAINING_GROUPS,
  TUTORIAL_ROLE_TRACKS,
  tutorialRoleKey,
} from "@/tutorials/tutorialCurriculum";

const ROLE_TAB_ORDER = ["admin", "doctor", "front-desk", "assistant"];

export default function SettingsTutorials() {
  const { clinicRole, isPlatformAdmin } = useAuth();
  const { startTutorial, completedModules, tutorialProgress } = useTutorial();
  const roleKey = tutorialRoleKey(clinicRole, isPlatformAdmin);
  const track = TUTORIAL_ROLE_TRACKS[roleKey];
  const completedQuickStart = track.quickStart.filter((item) => completedModules?.[item.id]).length;
  const quickStartComplete = completedQuickStart === track.quickStart.length;
  const [showAll, setShowAll] = useState(quickStartComplete);
  const [selectedTrainingRole, setSelectedTrainingRole] = useState(roleKey);
  const selectedGroups = useMemo(
    () => TUTORIAL_MORE_TRAINING_GROUPS[selectedTrainingRole] || [],
    [selectedTrainingRole],
  );

  useEffect(() => {
    setShowAll(quickStartComplete);
  }, [quickStartComplete, roleKey]);

  useEffect(() => {
    setSelectedTrainingRole(roleKey);
  }, [roleKey]);

  const launchLesson = (lessonItem) => {
    const fullTutorial = lessonItem.id === lessonItem.tutorialId && !lessonItem.stepIndexes;
    startTutorial(lessonItem.tutorialId, {
      progressId: lessonItem.id,
      stepIndexes: lessonItem.stepIndexes,
      resume: true,
      persistCompletion: fullTutorial,
    });
  };

  return (
    <div className="space-y-5" data-testid="settings-tutorials" data-tutorial-id="settings-tutorials">
      <header className="rounded-2xl border border-slate-200 bg-white px-5 py-5 shadow-sm sm:px-6">
        <div className="flex items-start gap-3">
          <span className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-teal-50 text-teal-700">
            <BookOpen className="h-5 w-5" strokeWidth={2.25} />
          </span>
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-wider text-teal-700">Training for {track.label}</p>
            <h1 className="mt-1 text-xl font-bold text-slate-950">Learn the work you need today</h1>
            <p className="mt-1 max-w-2xl text-sm leading-6 text-slate-600">
              Start with three short lessons. You can stop anytime and continue from the same place later.
            </p>
          </div>
        </div>
      </header>

      <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm" data-testid="tutorial-quick-start">
        <div className="border-b border-slate-100 px-5 py-4 sm:px-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="text-base font-bold text-slate-950">Quick start</h2>
              <p className="mt-1 text-sm text-slate-600">{track.description}</p>
            </div>
            <p className="text-sm font-semibold text-slate-700">{completedQuickStart} of 3 finished</p>
          </div>
          <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-slate-100" aria-hidden="true">
            <div
              className="h-full rounded-full bg-teal-600 transition-[width] duration-200"
              style={{ width: `${(completedQuickStart / track.quickStart.length) * 100}%` }}
            />
          </div>
        </div>

        <div className="divide-y divide-slate-100">
          {track.quickStart.map((lessonItem, index) => (
            <LessonRow
              key={lessonItem.id}
              lesson={lessonItem}
              number={index + 1}
              completed={!!completedModules?.[lessonItem.id]}
              progress={tutorialProgress?.[lessonItem.id]}
              onLaunch={launchLesson}
              prominent
            />
          ))}
        </div>
      </section>

      <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <button
          type="button"
          data-testid="tutorial-browse-all"
          onClick={() => setShowAll((current) => !current)}
          disabled={!quickStartComplete}
          className="flex min-h-14 w-full items-center justify-between gap-4 px-5 py-4 text-left hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-teal-500/40 disabled:cursor-not-allowed disabled:bg-slate-50/70 disabled:hover:bg-slate-50/70 sm:px-6"
          aria-expanded={showAll}
        >
          <span className="flex items-start gap-3">
            <span className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${quickStartComplete ? "bg-teal-50 text-teal-700" : "bg-slate-100 text-slate-400"}`}>
              {quickStartComplete ? <CheckCircle2 className="h-4 w-4" /> : <Lock className="h-4 w-4" />}
            </span>
            <span>
              <span className={`block text-sm font-bold ${quickStartComplete ? "text-slate-950" : "text-slate-500"}`}>More training</span>
              <span className="mt-0.5 block text-sm text-slate-500">
                {quickStartComplete
                  ? "Unlocked. Choose the workflow you need next."
                  : `Finish ${track.quickStart.length - completedQuickStart} more Quick Start ${track.quickStart.length - completedQuickStart === 1 ? "lesson" : "lessons"} to unlock.`}
              </span>
            </span>
          </span>
          {quickStartComplete ? (
            <ChevronDown className={`h-5 w-5 shrink-0 text-slate-400 transition-transform duration-200 ${showAll ? "rotate-180" : ""}`} />
          ) : null}
        </button>

        {showAll ? (
          <div className="border-t border-slate-100" data-testid="tutorial-all-lessons">
            <RoleTabs
              selectedRole={selectedTrainingRole}
              onSelectRole={setSelectedTrainingRole}
            />
            <div
              key={selectedTrainingRole}
              className="space-y-3 bg-slate-50/70 p-4 sm:p-5"
              data-testid={`tutorial-role-groups-${selectedTrainingRole}`}
            >
              {selectedGroups.map((group, index) => (
                <TrainingGroup
                  key={group.id}
                  group={group}
                  roleKey={selectedTrainingRole}
                  defaultOpen={index === 0}
                  completedModules={completedModules}
                  tutorialProgress={tutorialProgress}
                  onLaunch={launchLesson}
                />
              ))}
            </div>
          </div>
        ) : null}
      </section>
    </div>
  );
}

function RoleTabs({ selectedRole, onSelectRole }) {
  return (
    <div className="border-b border-slate-200 bg-white px-4 pt-3 sm:px-5" data-testid="tutorial-role-tabs">
      <div className="flex gap-1 overflow-x-auto" role="tablist" aria-label="Training role">
        {ROLE_TAB_ORDER.map((tabRole) => {
          const selected = selectedRole === tabRole;
          return (
            <button
              key={tabRole}
              type="button"
              role="tab"
              aria-selected={selected}
              data-testid={`tutorial-role-tab-${tabRole}`}
              onClick={() => onSelectRole(tabRole)}
              className={`min-h-11 shrink-0 rounded-t-lg border-b-2 px-4 py-2 text-sm font-semibold focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-500/40 ${selected ? "border-teal-600 bg-teal-50/70 text-teal-800" : "border-transparent text-slate-600 hover:bg-slate-50 hover:text-slate-900"}`}
            >
              {TUTORIAL_ROLE_TRACKS[tabRole].label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function TrainingGroup({ group, roleKey, defaultOpen, completedModules, tutorialProgress, onLaunch }) {
  const completedCount = group.lessons.filter((lessonItem) => completedModules?.[lessonItem.id]).length;
  return (
    <details
      open={defaultOpen}
      className="group overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm"
      data-testid={`tutorial-group-${roleKey}-${group.id}`}
    >
      <summary className="flex min-h-16 cursor-pointer list-none items-center justify-between gap-4 px-4 py-3 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-teal-500 sm:px-5">
        <span className="min-w-0">
          <span className="block text-sm font-bold text-slate-950">{group.label}</span>
          <span className="mt-0.5 block text-sm text-slate-500">{group.description}</span>
        </span>
        <span className="flex shrink-0 items-center gap-3">
          <span className="text-xs font-semibold text-slate-500">
            {completedCount} of {group.lessons.length} finished
          </span>
          <ChevronDown className="h-4 w-4 text-slate-400 transition-transform duration-200 group-open:rotate-180" />
        </span>
      </summary>
      <div className="divide-y divide-slate-100 border-t border-slate-100">
        {group.lessons.map((lessonItem) => (
          <LessonRow
            key={lessonItem.id}
            lesson={lessonItem}
            completed={!!completedModules?.[lessonItem.id]}
            progress={tutorialProgress?.[lessonItem.id]}
            onLaunch={onLaunch}
            compact
          />
        ))}
      </div>
    </details>
  );
}

function LessonRow({ lesson, number = null, completed, progress, onLaunch, prominent = false, compact = false }) {
  const tutorial = getTutorialById(lesson.tutorialId);
  if (!tutorial) return null;
  const inProgress = !!progress && !completed;
  const actionLabel = completed ? "Review" : inProgress ? "Resume" : "Start";

  return (
    <div
      className={`grid gap-3 px-5 py-4 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center sm:px-6 ${compact ? "bg-transparent" : ""}`}
      data-testid={`tutorial-lesson-${lesson.id}`}
    >
      <div className="flex min-w-0 items-start gap-3">
        {number ? (
          <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-bold ${completed ? "bg-teal-600 text-white" : "bg-slate-100 text-slate-600"}`}>
            {completed ? <CheckCircle2 className="h-4 w-4" /> : number}
          </span>
        ) : null}
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className={`${prominent ? "text-base" : "text-sm"} font-semibold text-slate-950`}>{lesson.name}</h3>
            {completed ? <span className="text-xs font-semibold text-teal-700">Finished</span> : null}
            {inProgress ? <span className="text-xs font-semibold text-amber-700">In progress</span> : null}
          </div>
          <p className="mt-1 text-sm leading-5 text-slate-600">{lesson.outcome}</p>
          <p className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-slate-500">
            <Clock3 className="h-3.5 w-3.5" />
            About {lesson.minutes} min
            {inProgress ? " · Continue where you stopped" : ""}
          </p>
        </div>
      </div>
      <button
        type="button"
        data-testid={`tutorial-launch-${lesson.id}`}
        onClick={() => onLaunch(lesson)}
        className={`inline-flex min-h-11 items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-teal-500/40 ${completed ? "border border-slate-300 bg-white text-slate-700 hover:bg-slate-50" : "bg-teal-600 text-white shadow-sm hover:bg-teal-700"}`}
      >
        {completed ? <RotateCcw className="h-4 w-4" /> : <PlayCircle className="h-4 w-4" />}
        {actionLabel}
      </button>
    </div>
  );
}
